import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Eye, ImagePlus, Link2, Loader2, PartyPopper, Save, Timer, Trash2, Users,
} from 'lucide-react';
import client from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { UPLOAD_MAX_LABEL, uploadTooLarge } from '../lib/mediaAsset';
import { WelcomePopupCard } from '../components/WelcomePopup';
import { useAdminPermissions } from './lib/permissions';
import { AdminSkeleton } from './components/PageState';

/** Karşılama penceresi — siteye girene gösterilen pop-up.
 *
 * Kayıt `provider_configs` → `welcome_popup` satırındadır ve kendi ucundan
 * yazılır (`PUT /admin/welcome-popup`); kurallar backend/services/welcome_popup.py
 * docstring'indedir. Önizleme sitedeki pencerenin **aynı** bileşenidir.
 */

const FIELDS = [
  'enabled', 'audience', 'frequency', 'pages', 'delay_seconds', 'title', 'body',
  'image_url', 'button_label', 'button_url',
];

const EMPTY = {
  enabled: false,
  audience: 'all',
  frequency: 'once',
  pages: 'home',
  delay_seconds: 1,
  title: '',
  body: '',
  image_url: '',
  button_label: '',
  button_url: '',
};

export const AUDIENCE_OPTIONS = [
  { value: 'all', label: 'Herkes' },
  { value: 'guest', label: 'Yalnızca giriş yapmamış ziyaretçiler' },
  { value: 'member', label: 'Yalnızca üyeler' },
];

export const FREQUENCY_OPTIONS = [
  { value: 'once', label: 'Bir kez (içerik değişene kadar)' },
  { value: 'daily', label: 'Günde bir kez' },
  { value: 'session', label: 'Her ziyarette bir kez' },
  { value: 'always', label: 'Her sayfa açılışında' },
];

export const PAGE_OPTIONS = [
  { value: 'home', label: 'Yalnızca ana sayfa' },
  { value: 'all', label: 'Sitenin her sayfası' },
];

const pick = (data) => FIELDS.reduce((out, key) => {
  out[key] = data?.[key] ?? EMPTY[key];
  return out;
}, {});

const WelcomePopupAdmin = () => {
  const { can } = useAdminPermissions();
  const { toast } = useToast();
  const canWrite = can('content.write');
  const [saved, setSaved] = useState(EMPTY);
  const [form, setForm] = useState(EMPTY);
  const [version, setVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get('/admin/welcome-popup');
      setSaved(pick(data));
      setForm(pick(data));
      setVersion(Number(data?.version) || 0);
    } catch (error) {
      toast({
        title: 'Karşılama penceresi alınamadı',
        description: error.response?.data?.detail || 'Lütfen sayfayı yenileyin.',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(
    () => FIELDS.some((key) => String(form[key]) !== String(saved[key])),
    [form, saved],
  );
  const set = (key) => (event) => {
    const value = event?.target ? event.target.value : event;
    setForm((current) => ({ ...current, [key]: value }));
  };
  const hasContent = Boolean(form.title || form.body || form.image_url);

  const uploadImage = async (file) => {
    if (!file) return;
    if (uploadTooLarge(file)) {
      toast({ title: 'Görsel yüklenemedi', description: `Dosya en fazla ${UPLOAD_MAX_LABEL} olabilir.` });
      return;
    }
    const body = new FormData();
    body.append('file', file);
    setUploading(true);
    try {
      const { data } = await client.post('/admin/uploads/images', body);
      setForm((current) => ({ ...current, image_url: data.url }));
      toast({ title: 'Görsel yüklendi', description: 'Yayına almak için Kaydet\'e basın.' });
    } catch (error) {
      toast({
        title: 'Yükleme başarısız',
        description: error.response?.data?.detail || 'Görsel yüklenemedi',
      });
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, delay_seconds: Number(form.delay_seconds) || 0 };
      const { data } = await client.put('/admin/welcome-popup', payload);
      setSaved(pick(data));
      setForm(pick(data));
      setVersion(Number(data?.version) || 0);
      toast({
        title: 'Karşılama penceresi kaydedildi',
        description: data.enabled
          ? 'Pencere sitede yayında.'
          : 'Pencere kapalı; sitede gösterilmiyor.',
      });
    } catch (error) {
      toast({
        title: 'Kaydedilemedi',
        description: error.response?.data?.detail || 'Lütfen alanları kontrol edin.',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <AdminSkeleton rows={6} label="Karşılama penceresi yükleniyor" />;

  return (
    <div className="admin-welcome-popup-page space-y-4">
      <div className="admin-page-heading">
        <div className="admin-page-heading-icon"><PartyPopper size={22} /></div>
        <div>
          <h1>Karşılama Penceresi</h1>
          <p>Siteye girene gösterilen duyuru penceresi: kampanya, yeni üye bonusu, bakım notu…</p>
        </div>
        <div className="admin-page-heading-meta">
          {saved.enabled ? 'Yayında' : 'Kapalı'}{version ? ` · sürüm ${version}` : ''}
        </div>
      </div>

      {!canWrite && (
        <div className="admin-card p-4 text-sm text-amber-200">
          Bu pencereyi görüntüleyebilirsiniz; değiştirmek için içerik yazma yetkisi gerekir.
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_420px]">
        <section className="admin-card p-4 space-y-5">
          <label className={`admin-switch${form.enabled ? ' on' : ''}`}>
            <input
              type="checkbox"
              checked={form.enabled}
              disabled={!canWrite}
              onChange={(event) => setForm((current) => ({ ...current, enabled: event.target.checked }))}
            />
            <span className="admin-switch-track" aria-hidden="true" />
            <span className="min-w-0">
              <span className="block text-sm font-semibold">Pencereyi sitede göster</span>
              <span className="block text-[11px] leading-relaxed text-slate-500 mt-0.5">
                Kapalıyken hiçbir ziyaretçiye gösterilmez. Başlık, metin ya da görselden
                en az biri girilmeden açılamaz.
              </span>
            </span>
          </label>

          <section className="admin-fieldset">
            <h3 className="admin-fieldset-title"><Users size={12} /> Kime ve ne zaman</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Kime gösterilsin">
                <select className="admin-input" value={form.audience} onChange={set('audience')} disabled={!canWrite}>
                  {AUDIENCE_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                </select>
              </Field>
              <Field
                label="Ne sıklıkla"
                hint="Pencerenin içeriği değiştiğinde daha önce kapatan oyuncuya da yeniden gösterilir."
              >
                <select className="admin-input" value={form.frequency} onChange={set('frequency')} disabled={!canWrite}>
                  {FREQUENCY_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                </select>
              </Field>
              <Field label="Hangi sayfada">
                <select className="admin-input" value={form.pages} onChange={set('pages')} disabled={!canWrite}>
                  {PAGE_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                </select>
              </Field>
              <Field label="Gecikme" hint="Sayfa açıldıktan kaç saniye sonra çıksın (0-30).">
                <span className="admin-affix block">
                  <input
                    type="number"
                    min={0}
                    max={30}
                    className="admin-input"
                    value={form.delay_seconds}
                    onChange={set('delay_seconds')}
                    disabled={!canWrite}
                  />
                  <span className="admin-affix-text"><Timer size={13} /></span>
                </span>
              </Field>
            </div>
          </section>

          <section className="admin-fieldset">
            <h3 className="admin-fieldset-title"><PartyPopper size={12} /> İçerik</h3>
            <div className="grid grid-cols-1 gap-4">
              <Field label="Başlık">
                <input
                  className="admin-input"
                  maxLength={120}
                  value={form.title}
                  onChange={set('title')}
                  disabled={!canWrite}
                  placeholder="Hoş geldiniz!"
                />
              </Field>
              <Field
                label="Metin"
                hint="Düz metin veya HTML yazabilirsiniz. HTML, site DOM'undan izole bir çerçevede çizilir; kaynak metin en fazla 2.000 karakterdir."
              >
                <textarea
                  className="admin-input"
                  rows={4}
                  maxLength={2000}
                  value={form.body}
                  onChange={set('body')}
                  disabled={!canWrite}
                  placeholder="İlk yatırımınıza %100 bonus sizi bekliyor."
                />
              </Field>
              <div>
                <span className="text-xs text-slate-400 block mb-1.5">Görsel</span>
                <div className="flex flex-wrap items-center gap-2">
                  <label
                    className={`admin-btn-secondary inline-flex cursor-pointer items-center gap-2 ${
                      uploading || !canWrite ? 'pointer-events-none opacity-60' : ''
                    }`}
                  >
                    {uploading ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />}
                    Görsel Yükle
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml,.svg"
                      className="sr-only"
                      disabled={uploading || !canWrite}
                      onChange={(event) => {
                        uploadImage(event.target.files?.[0]);
                        event.target.value = '';
                      }}
                    />
                  </label>
                  {form.image_url && (
                    <button
                      type="button"
                      className="admin-btn-secondary inline-flex items-center gap-2"
                      onClick={() => setForm((current) => ({ ...current, image_url: '' }))}
                      disabled={!canWrite}
                    >
                      <Trash2 size={15} /> Görseli Kaldır
                    </button>
                  )}
                </div>
                <input
                  className="admin-input mt-2"
                  value={form.image_url}
                  onChange={set('image_url')}
                  disabled={!canWrite}
                  placeholder="veya https:// ile başlayan görsel adresi"
                />
                <span className="mt-1.5 block text-[11px] leading-relaxed text-slate-500">
                  Görsel pencerenin üstünde tam genişlikte çizilir; tasarımı hazır bir
                  banner'ı başlık ve metin olmadan da kullanabilirsiniz. En fazla {UPLOAD_MAX_LABEL}.
                </span>
              </div>
            </div>
          </section>

          <section className="admin-fieldset">
            <h3 className="admin-fieldset-title"><Link2 size={12} /> Düğme (isteğe bağlı)</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Düğme yazısı">
                <input
                  className="admin-input"
                  maxLength={40}
                  value={form.button_label}
                  onChange={set('button_label')}
                  disabled={!canWrite}
                  placeholder="Hemen Katıl"
                />
              </Field>
              <Field
                label="Bağlantı"
                hint="Site içi sayfa (/promosyonlar) ya da https:// ile başlayan dış adres. Dış adres yeni sekmede açılır."
              >
                <input
                  className="admin-input"
                  value={form.button_url}
                  onChange={set('button_url')}
                  disabled={!canWrite}
                  placeholder="/promosyonlar"
                />
              </Field>
            </div>
          </section>

          <div className="flex justify-end">
            <button
              type="button"
              className="admin-btn-primary inline-flex items-center gap-2"
              onClick={save}
              disabled={!canWrite || !dirty || saving}
            >
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
              Kaydet
            </button>
          </div>
        </section>

        <section className="admin-card p-4" aria-labelledby="welcome-popup-preview">
          <h2 id="welcome-popup-preview" className="flex items-center gap-2 text-sm font-semibold">
            <Eye size={15} /> Önizleme
          </h2>
          <p className="mt-1 text-[11px] text-slate-500">
            Sitede şemanın renkleriyle çizilir; burada yaklaşık görünümdür.
          </p>
          <div className="admin-welcome-popup-preview mt-3 rounded-xl p-4" style={{ background: 'var(--v2-surface-2, rgba(0,0,0,0.55))' }}>
            {hasContent ? (
              <WelcomePopupCard
                popup={form}
                onClose={() => {}}
                onAction={(event) => event.preventDefault()}
              />
            ) : (
              <p className="py-10 text-center text-sm text-slate-500">
                Önizleme için başlık, metin ya da görsel girin.
              </p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="text-xs text-slate-400 block mb-1.5">{label}</span>
    {children}
    {hint && <span className="mt-1.5 block text-[11px] leading-relaxed text-slate-500">{hint}</span>}
  </label>
);

export default WelcomePopupAdmin;
